import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { usePageSeo } from '../hooks/usePageSeo'
import { useJsonLd } from '../hooks/useJsonLd'
import { buildServicePageGraph } from '../lib/schema'
import SimpleCarousel from '../components/shared/SimpleCarousel'
import { Button } from '@/components/ui/button'

const MODALITY_KEYS = [
  { key: 'cbt', icon: 'list' },
  { key: 'tfCbt', icon: 'user' },
  { key: 'playArt', icon: 'heart' },
  { key: 'neurosequential', icon: 'brain' },
  { key: 'arc', icon: 'badge' },
  { key: 'mbsr', icon: 'heart' },
  { key: 'multicultural', icon: 'user' },
  { key: 'dbt', icon: 'brain' },
  { key: 'imago', icon: 'heart' },
]

function ModalityIcon({ type }) {
  const common = 'w-8 h-8 text-white'
  if (type === 'list') {
    return (
      <svg className={common} fill="currentColor" viewBox="0 0 20 20" aria-hidden>
        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
      </svg>
    )
  }
  if (type === 'user') {
    return (
      <svg className={common} fill="currentColor" viewBox="0 0 20 20" aria-hidden>
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
      </svg>
    )
  }
  if (type === 'badge') {
    return (
      <svg className={common} fill="currentColor" viewBox="0 0 20 20" aria-hidden>
        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    )
  }
  if (type === 'brain') {
    return (
      <svg className={common} fill="currentColor" viewBox="0 0 20 20" aria-hidden>
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" />
      </svg>
    )
  }
  return (
    <svg className={common} fill="currentColor" viewBox="0 0 20 20" aria-hidden>
      <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
    </svg>
  )
}

const TherapyModality = () => {
  const t = useTranslation()
  const { isLoading } = useLanguage()
  const location = useLocation()

  const pageTitle = `${t('therapy.hero.title')} | VIVA Psychotherapy`
  const pageDescription =
    t('home.seo.therapyModalityRoute.description') || t('therapy.hero.description')

  usePageSeo({
    enabled: !isLoading,
    title: pageTitle,
    description: pageDescription,
    keywords: t('home.seo.therapyModalityRoute.keywords'),
    pathname: location.pathname
  })

  const therapySchema = useMemo(
    () =>
      buildServicePageGraph({
        pathname: location.pathname,
        pageName: pageTitle,
        pageDescription,
        serviceName: t('therapy.hero.title'),
        serviceDescription: t('therapy.hero.description'),
        faqItems: []
      }),
    [location.pathname, t, pageTitle, pageDescription, isLoading]
  )

  useJsonLd('data-therapy-modality-schema', therapySchema, !isLoading)

  // Show loading state if translations are still loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-offWhite">
        <main>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded mb-4"></div>
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-primary-offWhite" data-seo-ready="1">
      <main>
        {/* Hero Section */}
        <section className="bg-slate-700 py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Image */}
              <div className="order-2 lg:order-1">
                <img
                  src="/img/lidiana/Lidianawithpuppets-1024x819.jpg"
                  alt="Lidiana Cartolano with therapy puppets"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
              
              {/* Content */}
              <div className="order-1 lg:order-2 text-center lg:text-left">
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  {t('therapy.hero.title')}
                </h1>
                <p className="text-xl text-primary-lightGray mb-6">
                  {t('therapy.hero.subtitle')}
                </p>
                <p className="text-lg text-white/90 text-justify">
                  {t('therapy.hero.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Counseling Areas Section */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-16">
              {/* Children & Adolescents - Image Left, Content Right */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="order-2 lg:order-1">
                  <img
                    src="/img/random/pexels-photo-3662643-3662643-683x1024.jpg"
                    alt="Counseling Children & Adolescents"
                    className="w-full h-96 object-cover rounded-lg shadow-lg"
                  />
                </div>
                <div className="order-1 lg:order-2 pl-0 lg:pl-8">
                  <h3 className="text-3xl font-bold text-primary-darkGreen mb-6">
                    {t('therapy.counseling.children.title')}
                  </h3>
                  <div className="w-16 h-1 bg-primary-golden mb-6"></div>
                  <p className="text-lg text-gray-700 leading-relaxed text-justify">
                    {t('therapy.counseling.children.content')}
                  </p>
                </div>
              </div>

              {/* Adults - Image Right, Content Left */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="order-1 lg:order-1 pr-0 lg:pr-8">
                  <h3 className="text-3xl font-bold text-primary-darkGreen mb-6">
                    {t('therapy.counseling.adults.title')}
                  </h3>
                  <div className="w-16 h-1 bg-primary-golden mb-6"></div>
                  <p className="text-lg text-gray-700 leading-relaxed text-justify">
                    {t('therapy.counseling.adults.content')}
                  </p>
                </div>
                <div className="order-2 lg:order-2">
                  <img
                    src="/img/random/pexels-photo-4098369-4098369-683x1024.jpg"
                    alt="Counseling Adults"
                    className="w-full h-96 object-cover rounded-lg shadow-lg"
                  />
                </div>
              </div>

              {/* Online & In Person - Image Left, Content Right */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="order-2 lg:order-1">
                  <img
                    src="/img/random/Onlinevideotherapyandcounseling.jpeg"
                    alt="Online & In Person Counseling"
                    className="w-full h-96 object-cover rounded-lg shadow-lg"
                  />
                </div>
                <div className="order-1 lg:order-2 pl-0 lg:pl-8">
                  <h3 className="text-3xl font-bold text-primary-darkGreen mb-6">
                    {t('therapy.counseling.online.title')}
                  </h3>
                  <div className="w-16 h-1 bg-primary-golden mb-6"></div>
                  <p className="text-lg text-gray-700 leading-relaxed text-justify">
                    {t('therapy.counseling.online.content')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Therapy Modalities Carousel */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Intro (subtitle + content) */}
            <div className="mb-12 text-center">
              <h2 className="text-2xl font-semibold text-primary-darkGreen mb-4">
                {t('therapy.modalities.intro.subtitle')}
              </h2>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto text-justify">
                {t('therapy.modalities.intro.content')}
              </p>
            </div>

            <SimpleCarousel
              className="rounded-lg bg-white shadow-lg"
              autoPlay
              infinite
              showCounter
              slides={MODALITY_KEYS.map(({ key, icon }) => (
                <div key={key} className="p-8">
                  <div className="flex items-start">
                    <div className="mr-6 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg bg-primary-golden">
                      <ModalityIcon type={icon} />
                    </div>
                    <div>
                      <h3 className="mb-4 text-2xl font-bold text-primary-darkGreen">
                        {t(`therapy.modalities.${key}.title`)}
                      </h3>
                      <p className="text-justify text-lg leading-relaxed text-gray-700">
                        {t(`therapy.modalities.${key}.description`)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            />
          </div>
        </section>

        {/* Call to Action */}
        <section className="bg-primary-lightGray py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-primary-darkGreen mb-6">
              {t('therapy.cta.title')}
            </h2>
            <p className="text-lg text-primary-darkGray mb-8">
              {t('therapy.cta.description')}
            </p>
            <Button type="button" variant="brand">
              {t('therapy.cta.button')}
            </Button>
          </div>
        </section>
      </main>
    </div>
  )
}

export default TherapyModality
